import React, { createContext, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { locationChangeBegin, locationChangeEnd } from 'store/__router/slice';

import { getLanguage, getMarket } from '@travel/i18n';

import { useAppDispatch, useAppSelector } from 'store/hooks';

import type { Action, Location } from 'history';
import type { ReactNode } from 'react';
import type { RouterHistory } from './types';

type Props = {
  router: { resolve: (location: Location) => Promise<ReactNode> };
  history: RouterHistory;
  children: ReactNode;
};

type RouterContextType = {
  history: RouterHistory;
};

const RouterContext = createContext<RouterContextType>({} as RouterContextType);

RouterContext.displayName = 'Router';

function Router(props: Props) {
  const { router, history, children } = props;
  const dispatch = useAppDispatch();
  const currentLocation = useRef<Location>();
  const currentAction = useRef<Action>();
  const [Component, setComponent] = useState(children);

  const market = useAppSelector(getMarket);
  const language = useAppSelector(getLanguage);

  useEffect(() => {
    const unlisten = history.listen((location, action) => {
      const shouldUpdate = action === 'POP' || (location?.state?.shouldUpdate ?? true);

      if (!shouldUpdate) {
        return;
      }

      dispatch(locationChangeBegin());
      currentLocation.current = location;
      currentAction.current = action;

      router.resolve(location).then((children) => {
        // XXX: if location doesn't match current location, it means a user triggered location change
        // before the previous one has been resolved. In this situation we skip the previous location change.
        if (location === currentLocation.current && action === currentAction.current) {
          dispatch(locationChangeEnd({ location, action }));
          setComponent(children);
        }
      });
    });

    return () => unlisten();
  }, [dispatch, history, language, market, router]);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      // We need to stop browser's default scroll restoration, because browser will restore the scroll position right
      // after history.push (or pop/...), but sometimes we don't render the next page immediately. This will cause
      // unexpected flash on previous page
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // XXX: This is a hacky solution. I'm using useLayoutEffect here is just to make sure that if any child component
  // wants to overwrite the scroll position, it can use useEffect because useEffect will be excuted after useLayoutEffect.
  // We need to find another solution and change it to use useEffect if this solution brings any performance issue
  useLayoutEffect(() => {
    if (currentAction.current !== 'REPLACE') {
      window.scrollTo(0, 0);
    }
  }, [Component]);

  return <RouterContext.Provider value={{ history }}>{Component}</RouterContext.Provider>;
}

export { RouterContext };
export default Router;
